import { type FC, useCallback } from "react";
import classNames from "classnames";
import { Controller, FormProvider, useForm } from "react-hook-form";
import SearchPeriodSelector from "~/features/orders/orderList/components/OrderListSearchBox/SearchPeriodSelector/SearchPeriodSelector";
import {
  OrderbookLineItemStatus,
  OrderLineStatusFilter,
} from "~/features/orderbook/components/OrderbookSearchBox/components/OrderLineStatusFilter";
import { getPredefinedPeriodDates, PredefinedDate } from "~/features/orders/common/utils/predefinedDates";
import { useOnMount } from "~/common/hooks/useOnMount";
import {
  makeSearchByValidator,
  QueryType,
  type SearchByData,
  SearchByInput,
} from "~/features/orderbook/components/OrderbookSearchBox/components/SearchByInput/SearchByInput";
import { Button } from "~/common/mui";
import { IconType } from "~/common/components/icon/IconType";
import { isDateValidForOrderSearch } from "~/features/orders/common/utils/IsDateValidForOrderSearch";
import classes from "~/features/orderbook/components/OrderbookSearchBox/OrderbookSearchBox.module.less";
import { SearchCard } from "~/common/components/SearchCard";
import { isMultiAccountOrderSearchEnabled } from "~/utils/features";
import {
  getInitialAccounts,
  MultiAccountSelect,
} from "~/features/orders/orderList/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSelect";
import { type SingleAccountData } from "~/features/orders/common/DownloadOrderHistoryService/types";
import { useMultiAccountSearchData } from "~/features/orders/orderList/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSearchDataProvider";
import {
  ConfirmedDeliveryLineFilter,
  OrderbookConfirmedDeliveryStatus,
} from "~/features/orderbook/components/OrderbookSearchBox/components/ConfirmedDeliveryLineFilter";
import { useTranslation } from "react-i18next";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";

interface Props {
  isLoading?: boolean;
  className?: string;
  onSubmit: (data: OrderbookSearchBoxData) => void;
  disabled?: boolean;
}

export interface OrderbookSearchBoxData {
  query: string;
  queryType: QueryType;
  dateFrom?: Date;
  dateTo?: Date;
  lineStatus?: string;
  accounts?: SingleAccountData[];
  confirmedLines: string;
  deliveryDate?: string;
}

export interface OrderbookSearchFormData {
  chosenDate: { start: Date | undefined; end: Date | undefined };
  isCustomDate: boolean;
  isPredefinedDate: boolean;
  lineStatusOption: string;
  predefinedDate: PredefinedDate;
  searchBy: SearchByData;
  confirmedLines: string;
  accounts: string[];
}

const INITIAL_SEARCH_FORM_DATA: OrderbookSearchFormData = {
  predefinedDate: PredefinedDate.LAST_30_DAYS,
  lineStatusOption: OrderbookLineItemStatus.ALL,
  chosenDate: { start: undefined, end: undefined },
  isCustomDate: false,
  isPredefinedDate: true,
  searchBy: {
    query: "",
    type: QueryType.PURCHASE_NO,
  },
  accounts: getInitialAccounts(),
  confirmedLines: OrderbookConfirmedDeliveryStatus.ALL,
};

const AVAILABLE_QUERY_TYPES = [
  QueryType.PURCHASE_NO,
  QueryType.SALES_NO,
  QueryType.DESIGNATION,
  QueryType.SAP_MATERIAL_NO,
  QueryType.CUSTOMER_MATERIAL_NO,
];

type GetDatesFromFormProp = Pick<OrderbookSearchFormData, "isCustomDate" | "chosenDate" | "predefinedDate">;
export const getDatesFromForm = (form: GetDatesFromFormProp) => {
  // check if custom date is set
  if (form.isCustomDate) {
    return {
      dateFrom: form.chosenDate.start,
      dateTo: form.chosenDate.end,
    };
  } else {
    return getPredefinedPeriodDates(form.predefinedDate);
  }
};

const OrderbookSearchBox: FC<Props> = ({ isLoading, onSubmit, className, disabled }) => {
  const { t } = useTranslation();
  const multiAccountData = useMultiAccountSearchData();

  // const showUnconfirmedLinesFilter = window.SCHA.abmonitor?.showUnconfirmedLinesFilter ?? false;
  //
  // console.log({showUnconfirmedLinesFilter});

  const formHooks = useForm({ defaultValues: INITIAL_SEARCH_FORM_DATA, mode: "onTouched" });

  const getValues = formHooks.getValues;

  const onSubmitCb = useCallback(() => {
    const formData = getValues() as OrderbookSearchFormData;
    const { lineStatusOption, searchBy, accounts: formAccounts, confirmedLines } = formData;

    let accounts:
      | {
          accountNumber: string;
          endCustomer: string;
          salesOrg: string;
        }[]
      | undefined = undefined;
    if (formAccounts) {
      accounts = formAccounts
        .map((str) => str.split("-"))
        .map((parts) => ({
          accountNumber: parts[0],
          endCustomer: parts[1],
          salesOrg: parts[2],
        }));
    }

    const requestPayload: OrderbookSearchBoxData = {
      query: searchBy.query,
      queryType: searchBy.type,
      lineStatus: lineStatusOption,
      ...getDatesFromForm(formData),
      accounts,
      confirmedLines: confirmedLines,
    };

    onSubmit(requestPayload);
  }, [getValues]);

  useOnMount(onSubmitCb);

  return (
    <FormProvider {...formHooks}>
      <SearchCard className={classNames(className, classes.card)} data-test-id={"abmonitor-search-box"}>
        <form onSubmit={formHooks.handleSubmit(onSubmitCb)}>
          <div className={classes.row}>
            <Controller
              render={({ field: { value, onChange, onBlur, ref }, fieldState: { error } }) => (
                <SearchByInput
                  className={classes.searchBy}
                  value={value}
                  onChange={onChange}
                  visibleOptions={AVAILABLE_QUERY_TYPES}
                  pageVariant={"abmonitor"}
                  error={!!error}
                  helperLabel={error?.message}
                  onBlur={onBlur}
                  ref={ref}
                />
              )}
              name={"searchBy"}
              defaultValue={INITIAL_SEARCH_FORM_DATA.searchBy}
              control={formHooks.control}
              rules={{
                validate: makeSearchByValidator(t, "abmonitor"),
              }}
            />

            <SearchPeriodSelector
              formHooks={formHooks}
              isDateValidForSelection={isDateValidForOrderSearch}
              className={classes.searchPeriod}
            />
          </div>

          <div className={classes.row}>
            <div className={classes.searchStatus}>
              <OrderLineStatusFilter />
            </div>

            {isMultiAccountOrderSearchEnabled() && (
              <div className={classes.accountSelect}>
                <Controller
                  render={({ field: { value, onChange } }) => <MultiAccountSelect values={value} onChange={onChange} />}
                  name={"accounts"}
                  defaultValue={INITIAL_SEARCH_FORM_DATA.accounts}
                  control={formHooks.control}
                />
              </div>
            )}

            {multiAccountData.showUnconfirmedLinesFilter && (
              <div className={classes.searchStatus}>
                <ConfirmedDeliveryLineFilter />
              </div>
            )}
          </div>

          <div className={classes.buttonRow}>
            <Button
              variant={"text"}
              data-test-id={"search-form-reset"}
              startIcon={<DefaultButtonIcon type={IconType.Reload} />}
              onClick={() => {
                formHooks.reset(INITIAL_SEARCH_FORM_DATA);
                onSubmitCb();
              }}
            >
              {t("common.button.resetForm")}
            </Button>

            <Button
              disabled={window.SCHA.config.disableAllWS || disabled || isLoading}
              type={"submit"}
              data-test-id={"abmonitor-search-submit"}
            >
              {t("quoteListPage.applyBtn")}
            </Button>
          </div>
        </form>
      </SearchCard>
    </FormProvider>
  );
};

export default OrderbookSearchBox;
